import { supabaseClient } from '@/configs/supabase'
import { Row } from '@/libs/supabase/supabase.interface'
import { SupabaseClient } from '@supabase/supabase-js'
import { generateCustomerId, generatePricingId } from '@/utils/idGenerators'
import {
  ICreateCustomerInput,
  IUpdateCustomerInput,
  ICreateCustomerPricingInput,
  IUpdateCustomerPricingInput,
  ICustomerWithPricing
} from './customer.interface'

// Helper function to get the next available ranking
async function getNextAvailableRanking(
  db: SupabaseClient = supabaseClient
): Promise<number> {
  // Get all active (non-deleted) customers with rankings
  const { data: activeCustomers, error } = await db
    .from('Customer')
    .select('ranking')
    .is('deleted_at', null)
    .not('ranking', 'is', null)
    .order('ranking', { ascending: true })

  if (error) {
    console.error('Error fetching rankings:', error)
    return 1 // Default to 1 if error
  }

  if (!activeCustomers || activeCustomers.length === 0) {
    return 1 // First customer gets ranking 1
  }

  // Find the first gap in rankings or return next number
  const rankings = activeCustomers
    .map(c => c.ranking)
    .filter(r => r !== null)
    .sort((a, b) => a - b)

  for (let i = 0; i < rankings.length; i++) {
    if (rankings[i] !== i + 1) {
      return i + 1 // Found a gap
    }
  }

  return rankings.length + 1 // No gaps, return next number
}

// Function to reorder customer rankings
export async function reorderCustomerRankings(
  customerRankings: { id: string; ranking: number }[],
  db: SupabaseClient = supabaseClient
): Promise<boolean> {
  try {
    // Update each customer's ranking
    for (const { id, ranking } of customerRankings) {
      const { error } = await db
        .from('Customer')
        .update({
          ranking: ranking,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .is('deleted_at', null)

      if (error) {
        console.error(`Error updating ranking for customer ${id}:`, error)
        return false
      }
    }

    return true
  } catch (error) {
    console.error('Error reordering customer rankings:', error)
    return false
  }
}

const handleError = (error: any, message: string) => {
  console.error(message, error)
  return null
}

export async function getCustomers(db: SupabaseClient = supabaseClient) {
  const { data, error } = await db
    .from('Customer')
    .select(
      `
      *,
      pricing:CustomerPricing(*)
    `
    )
    .is('deleted_at', null)
    .order('ranking', { ascending: true, nullsFirst: false })
    .order('name', { ascending: true })

  if (error) {
    return handleError(error, 'Error fetching customers:')
  }

  // Transform the data to match our interface
  const customersWithPricing =
    data?.map(customer => ({
      ...customer,
      pricing:
        customer.pricing && customer.pricing.length > 0
          ? customer.pricing[0]
          : null
    })) || []

  return customersWithPricing as ICustomerWithPricing[]
}

export async function searchCustomers(
  searchTerm: string,
  customerType?: 'REGULAR' | 'GENERAL',
  db: SupabaseClient = supabaseClient
) {
  let query = db
    .from('Customer')
    .select(
      `
      *,
      pricing:CustomerPricing(*)
    `
    )
    .is('deleted_at', null)
    .ilike('name', `%${searchTerm}%`)

  if (customerType) {
    query = query.eq('type', customerType)
  }

  const { data, error } = await query.order('name', { ascending: true })

  if (error) {
    return handleError(error, 'Error searching customers:')
  }

  // Transform the data to match our interface (same as getCustomers)
  const customersWithPricing =
    data?.map(customer => ({
      ...customer,
      pricing:
        customer.pricing && customer.pricing.length > 0
          ? customer.pricing[0]
          : null
    })) || []

  return customersWithPricing as ICustomerWithPricing[]
}

export async function getCustomersByType(
  customerType: 'REGULAR' | 'GENERAL',
  db: SupabaseClient = supabaseClient
) {
  const { data, error } = await db
    .from('Customer')
    .select(
      `
      *,
      pricing:CustomerPricing(*)
    `
    )
    .eq('type', customerType)
    .is('deleted_at', null)
    .order('ranking', { ascending: true, nullsFirst: false })
    .order('name', { ascending: true })

  if (error) {
    return handleError(error, 'Error fetching customers by type:')
  }

  // Transform the data to match our interface (same as getCustomers)
  const customersWithPricing =
    data?.map(customer => ({
      ...customer,
      pricing:
        customer.pricing && customer.pricing.length > 0
          ? customer.pricing[0]
          : null
    })) || []
  return customersWithPricing as ICustomerWithPricing[]
}

export async function getCustomerById(
  id: string,
  db: SupabaseClient = supabaseClient
) {
  const { data, error } = await db
    .from('Customer')
    .select(
      `
      *,
      pricing:CustomerPricing(*)
    `
    )
    .eq('id', id)
    .is('deleted_at', null)
    .single()



  if (error) {
    return handleError(error, 'Error fetching customer:')
  }

  // Transform the data to match our interface
  const customerWithPricing = {
    ...data,
    pricing:
      data.pricing && data.pricing.length > 0
        ? data.pricing[0]
        : null
  }


  return customerWithPricing as ICustomerWithPricing
}

export async function createCustomer(
  customerData: ICreateCustomerInput,
  db: SupabaseClient = supabaseClient
) {
  // Generate customer ID using nanoid with cust_ prefix
  const customerId = generateCustomerId()
  const timestamp = new Date().toISOString()

  // Get next available ranking
  const ranking = await getNextAvailableRanking(db)

  const { data, error } = await db
    .from('Customer')
    .insert({
      id: customerId,
      name: customerData.name,
      type: customerData.type,
      ranking: ranking,
      metadata: customerData.metadata || null,
      created_at: timestamp,
      updated_at: timestamp
    })
    .select()
    .single()

  if (error) {
    console.error('Database error creating customer:', error)
    console.error('Customer data:', {
      customerId,
      name: customerData.name,
      type: customerData.type,
      ranking
    })
    return handleError(error, 'Error creating customer:')
  }

  console.log('Customer created successfully with ranking:', data)
  return data as Row<'Customer'>
}

export async function updateCustomer(
  id: string,
  customerData: IUpdateCustomerInput,
  db: SupabaseClient = supabaseClient
) {
  const { data, error } = await db
    .from('Customer')
    .update({
      ...customerData,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    return handleError(error, 'Error updating customer:')
  }

  return data as Row<'Customer'>
}

export async function deleteCustomer(
  id: string,
  db: SupabaseClient = supabaseClient
) {
  // First, get the customer being deleted to know its ranking
  const { data: customerToDelete, error: fetchError } = await db
    .from('Customer')
    .select('ranking')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (fetchError) {
    return handleError(fetchError, 'Error fetching customer to delete:')
  }

  // Delete the customer (soft delete)
  const { data, error } = await db
    .from('Customer')
    .update({
      deleted_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    return handleError(error, 'Error deleting customer:')
  }

  // If the deleted customer had a ranking, reorder remaining customers
  if (customerToDelete.ranking) {
    await reorderRankingsAfterDeletion(customerToDelete.ranking, db)
  }

  return data as Row<'Customer'>
}

// Helper function to reorder rankings after deletion
async function reorderRankingsAfterDeletion(
  deletedRanking: number,
  db: SupabaseClient = supabaseClient
): Promise<boolean> {
  try {
    // Get all customers with rankings higher than the deleted customer
    const { data: customersToReorder, error: fetchError } = await db
      .from('Customer')
      .select('id, ranking')
      .gt('ranking', deletedRanking)
      .is('deleted_at', null)
      .order('ranking', { ascending: true })

    if (fetchError) {
      console.error('Error fetching customers to reorder:', fetchError)
      return false
    }

    if (!customersToReorder || customersToReorder.length === 0) {
      return true // No customers to reorder
    }

    // Update each customer's ranking by decreasing it by 1
    for (const customer of customersToReorder) {
      const { error: updateError } = await db
        .from('Customer')
        .update({
          ranking: customer.ranking - 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', customer.id)

      if (updateError) {
        console.error(
          `Error updating ranking for customer ${customer.id}:`,
          updateError
        )
        return false
      }
    }

    return true
  } catch (error) {
    console.error('Error reordering rankings after deletion:', error)
    return false
  }
}

export async function createCustomerPricing(
  pricingData: ICreateCustomerPricingInput,
  db: SupabaseClient = supabaseClient
) {
  // Generate pricing ID using nanoid with price_ prefix
  const pricingId = generatePricingId()
  const timestamp = new Date().toISOString()

  const { data, error } = await db
    .from('CustomerPricing')
    .insert({
      id: pricingId,
      customer_id: pricingData.customerId,
      large_ice_cube_price_thb: pricingData.largeIceCubePriceTHB,
      small_ice_cube_price_thb: pricingData.smallIceCubePriceTHB,
      crushed_ice_price_thb: pricingData.crushedIcePriceTHB,
      is_default_pricing: pricingData.isDefaultPricing || false,
      created_at: timestamp,
      updated_at: timestamp
    })
    .select()
    .single()

  if (error) {
    console.error('Database error creating pricing:', error)
    console.error('Pricing data:', {
      pricingId,
      customerId: pricingData.customerId,
      prices: {
        large: pricingData.largeIceCubePriceTHB,
        small: pricingData.smallIceCubePriceTHB,
        crushed: pricingData.crushedIcePriceTHB
      }
    })
    return handleError(error, 'Error creating customer pricing:')
  }

  console.log('Pricing created successfully:', data)
  return data as Row<'CustomerPricing'>
}

export async function updateCustomerPricing(
  customerId: string,
  pricingData: IUpdateCustomerPricingInput,
  db: SupabaseClient = supabaseClient
) {
  const { data, error } = await db
    .from('CustomerPricing')
    .update({
      ...(pricingData.largeIceCubePriceTHB !== undefined && {
        large_ice_cube_price_thb: pricingData.largeIceCubePriceTHB
      }),
      ...(pricingData.smallIceCubePriceTHB !== undefined && {
        small_ice_cube_price_thb: pricingData.smallIceCubePriceTHB
      }),
      ...(pricingData.crushedIcePriceTHB !== undefined && {
        crushed_ice_price_thb: pricingData.crushedIcePriceTHB
      }),
      ...(pricingData.isDefaultPricing !== undefined && {
        is_default_pricing: pricingData.isDefaultPricing
      }),
      updated_at: new Date().toISOString()
    })
    .eq('customer_id', customerId)
    .select()
    .single()

  if (error) {
    return handleError(error, 'Error updating customer pricing:')
  }

  return data as Row<'CustomerPricing'>
}

export async function getDefaultPricing(db: SupabaseClient = supabaseClient) {
  const { data, error } = await db
    .from('CustomerPricing')
    .select('*')
    .eq('is_default_pricing', true)
    .single()

  if (error) {
    return handleError(error, 'Error fetching default pricing:')
  }

  return data as Row<'CustomerPricing'>
}

export async function getDefaultCustomerPricing(
  db: SupabaseClient = supabaseClient
) {

  const { data, error } = await db
    .from('Customer')
    .select(
      `
      *,
      pricing:CustomerPricing(*)
    `
    )
    .eq('id', 'default')
    .is('deleted_at', null)
    .single()



  if (error) {
    return handleError(error, 'Error fetching default customer pricing:')
  }

  if (data?.pricing && data.pricing.length > 0) {

    return data.pricing[0] as Row<'CustomerPricing'>
  }


  return null
}

export async function createCustomerWithPricing(
  customerData: ICreateCustomerInput,
  pricingData: Omit<ICreateCustomerPricingInput, 'customerId'>,
  db: SupabaseClient = supabaseClient
) {
  // First create the customer
  const customer = await createCustomer(customerData, db)

  if (!customer) {
    return null
  }

  // Then create the pricing
  const pricing = await createCustomerPricing(
    {
      ...pricingData,
      customerId: customer.id
    },
    db
  )

  if (!pricing) {
    // If pricing creation fails, we should ideally rollback the customer creation
    // For now, we'll just return the customer without pricing
    return customer
  }

  return {
    ...customer,
    pricing
  } as ICustomerWithPricing
}

export async function getCustomersWithoutPricing(
  db: SupabaseClient = supabaseClient
) {
  const { data, error } = await db
    .from('Customer')
    .select(
      `
      *,
      pricing:CustomerPricing(*)
    `
    )
    .is('deleted_at', null)
    .is('pricing.customer_id', null)
    .order('name', { ascending: true })

  if (error) {
    return handleError(error, 'Error fetching customers without pricing:')
  }

  return data as ICustomerWithPricing[]
}

export async function checkOrCreateCustomer(
  customerId: string,
  customerName?: string,
  customerType: 'REGULAR' | 'GENERAL' = 'GENERAL',
  db: SupabaseClient = supabaseClient
) {
  const { data: existingCustomer, error: customerError } = await db
    .from('Customer')
    .select('id')
    .eq('id', customerId)

  if (customerError) {
    return handleError(customerError, 'Error checking for existing Customer:')
  }

  if (existingCustomer.length === 0) {
    const timestamp = new Date()
    const { error: newCustomerError } = await db
      .from('Customer')
      .insert({
        id: customerId,
        name: customerName || customerId,
        type: customerType,
        created_at: timestamp.toISOString(),
        updated_at: timestamp.toISOString()
      })
      .single()

    if (newCustomerError) {
      return handleError(newCustomerError, 'Error creating new Customer:')
    }

    // Create default pricing for the new customer
    // First try to get default pricing from 'default' customer
    const defaultCustomerPricing = await getDefaultCustomerPricing(db)

    if (!defaultCustomerPricing) {
      return handleError(newCustomerError, 'Error defaultCustomerPricing:')
    }

    // Create pricing for the new customer
    const pricingId = generatePricingId()
    const { error: pricingError } = await db
      .from('CustomerPricing')
      .insert({
        id: pricingId,
        customer_id: customerId,
        large_ice_cube_price_thb:
          defaultCustomerPricing.large_ice_cube_price_thb,
        small_ice_cube_price_thb:
          defaultCustomerPricing.small_ice_cube_price_thb,
        crushed_ice_price_thb: defaultCustomerPricing.crushed_ice_price_thb,
        is_default_pricing: false,
        created_at: timestamp.toISOString(),
        updated_at: timestamp.toISOString()
      })
      .single()

    if (pricingError) {
      console.error('Error creating customer pricing:', pricingError)
      // Don't fail the whole operation if pricing creation fails
    }
  }

  return true
}
/**
 * Ensures that the default customer exists and has pricing configured.
 * Throws an error if the default customer or its pricing is not found.
 *
 * @param db - Supabase client instance
 * @throws {Error} When default customer is not found
 * @throws {Error} When default customer exists but has no pricing
 * @returns Promise<true> if default customer and pricing exist
 */
export async function ensureDefaultCustomer(
  db: SupabaseClient = supabaseClient
) {
  // Check if default customer exists (use array query to avoid PGRST116 error)
  const { data: defaultCustomers, error: checkError } = await db
    .from('Customer')
    .select(
      `
      *,
      pricing:CustomerPricing(*)
    `
    )
    .eq('id', 'default')
    .is('deleted_at', null)

  if (checkError) {
    console.error('Error checking for default customer:', checkError)
    throw new Error(
      `Failed to check for default customer: ${checkError.message}`
    )
  }

  const defaultCustomer =
    defaultCustomers && defaultCustomers.length > 0 ? defaultCustomers[0] : null

  if (!defaultCustomer) {
    // Throw error instead of creating default customer
    throw new Error(
      'Default customer not found. Please ensure the default customer exists in the database.'
    )
  }

  // Check if default customer has pricing
  if (
    !defaultCustomer.pricing ||
    defaultCustomer.pricing.length === 0
  ) {
    // Throw error instead of creating default pricing
    throw new Error(
      'Default customer exists but has no pricing. Please ensure default pricing is configured.'
    )
  }

  return true
}
